import React, { useEffect, useMemo, useRef, useState } from 'react';
import { parseEstDate, formatDeliveryDate, getCutoffTime } from 'utils/deliveryDates';
import { COLORS, FONTS } from 'styles/theme';
import { useMediaQuery } from 'hooks/useMediaQuery';
import * as mealPlanService from 'services/mealPlanService';
import './SkipDeliveriesModal.css';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const INK = '#2B2118';
const INK_MUTED = '#5C4D40';
const ACTION = '#8A3F0B';
const PRIMARY = '#A24A12';
const DIVIDER = '#E8E2DC';
const TRUCK_GREEN = '#2f8a3e';
const TRUCK_RED = '#C0392B';
const DELIVERING = { bg: '#FDDDB8', border: '#E0A05A', text: '#4A2A0A' };
const SKIPPED = { bg: '#EFEDEA', border: '#8C857D', text: '#4B4640' };
const NO_MEALS = { bg: '#FFFFFF', border: '#EFCB9E', text: '#8A5A2B' };

const todayEt = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
// Plain calendar-date math in UTC so the DST change can't shift a day.
const addDays = (iso, n) => {
    const d = new Date(`${iso}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
};
const weekdayOf = (iso) => new Date(`${iso}T00:00:00Z`).getUTCDay();
const monthOf = (iso) => iso.slice(0, 7);

// Full calendar months from firstMonth through lastMonth ('YYYY-MM'), each split into Sunday-start rows;
// days outside the month are null.
const buildCalendar = (schedule, firstMonth, lastMonth) => {
    const entries = new Map();
    schedule.forEach(({ deliveryDay, dates }) => dates.forEach(d => entries.set(d.date, { ...d, deliveryDay })));
    const months = [];
    for (let date = `${firstMonth}-01`; monthOf(date) <= lastMonth; date = addDays(date, 1)) {
        let month = months[months.length - 1];
        if (!month || month.key !== monthOf(date)) {
            month = { key: monthOf(date), label: parseEstDate(date).format('MMMM YYYY'), rows: [] };
            months.push(month);
        }
        let row = month.rows[month.rows.length - 1];
        if (!row || weekdayOf(date) === 0) {
            row = Array(7).fill(null);
            month.rows.push(row);
        }
        row[weekdayOf(date)] = { date, entry: entries.get(date) };
    }
    return months;
};

const ArrowButton = ({ direction, disabled, onClick }) => (
    <button className="sdm-bare"
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={direction === 'prev' ? 'Previous month' : 'Next month'}
        style={{
            width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: 0, borderRadius: '8px', background: 'none', padding: 0,
            color: disabled ? '#d3ccc5' : ACTION, cursor: disabled ? 'default' : 'pointer',
        }}
    >
        <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d={direction === 'prev' ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7'} />
        </svg>
    </button>
);

const TruckIcon = ({ size, color = TRUCK_GREEN }) => (
    <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 6h12v10H2z" />
        <path d="M14 10h4l4 4v2h-8z" />
        <circle cx="6.5" cy="17.5" r="1.8" fill="#fff" />
        <circle cx="17.5" cy="17.5" r="1.8" fill="#fff" />
    </svg>
);

const linkButton = {
    padding: 0, margin: 0, border: 0, background: 'none', fontFamily: 'inherit',
    fontWeight: 700, color: ACTION, textDecoration: 'underline', cursor: 'pointer',
};

const SkipDeliveriesModal = ({ customerToken, currentWeekDates = [], datesWithOrders = [], onSaved, onClose }) => {
    const isWide = useMediaQuery('(min-width: 720px)');
    const [schedule, setSchedule] = useState(null);
    const [windowEnd, setWindowEnd] = useState(null);
    const [draft, setDraft] = useState(new Set());
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [monthKey, setMonthKey] = useState(null);

    useEffect(() => {
        mealPlanService.getSkips(customerToken)
            .then(({ schedule: loaded, windowEnd: end }) => {
                setSchedule(loaded);
                setWindowEnd(end);
                setDraft(new Set(loaded.flatMap(({ dates }) => dates.filter(d => d.skipped).map(d => d.date))));
            })
            .catch(() => setError('Could not load your upcoming deliveries.'));
    }, [customerToken]);

    // Stops the page behind the sheet from scrolling on phones.
    useEffect(() => {
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = previous; };
    }, []);
    const touchStartX = useRef(null);

    const allDates = useMemo(() => (schedule || []).flatMap(({ dates }) => dates), [schedule]);
    const saved = useMemo(() => new Set(allDates.filter(d => d.skipped).map(d => d.date)), [allDates]);
    const locked = useMemo(() => new Set(allDates.filter(d => d.locked).map(d => d.date)), [allDates]);
    const today = todayEt();
    const months = useMemo(() => {
        if (!schedule) return [];
        const last = [windowEnd, ...allDates.map(d => d.date)].filter(Boolean).sort().pop() || today;
        return buildCalendar(schedule, monthOf(today), monthOf(last));
    }, [schedule, windowEnd, allDates, today]);
    const foundIndex = months.findIndex(m => m.key === (monthKey || monthOf(today)));
    const monthIndex = foundIndex === -1 ? 0 : foundIndex;
    const month = months[monthIndex];

    const skip =[...draft].filter(d => !saved.has(d));
    const unskip = [...saved].filter(d => !draft.has(d));
    const hasChanges = skip.length + unskip.length > 0;
    const editableSkipped = [...draft].filter(d => !locked.has(d));

    const currentWeek = useMemo(() => new Set(currentWeekDates), [currentWeekDates]);
    const withMeals = useMemo(() => new Set(datesWithOrders), [datesWithOrders]);
    // This week follows the meals actually on the plan; later weeks follow the subscription schedule.
    // Meals moved onto a non-plan day this week can be cleared too, until that day's cutoff.
    const cellState = (date, entry) => {
        if (currentWeek.has(date)) {
            if (entry && !entry.locked) return 'editable';
            if (!entry && withMeals.has(date) && Date.now() < getCutoffTime(date).getTime()) return 'editable';
            if (entry && !withMeals.has(date)) return 'emptySkipped';
            return withMeals.has(date) ? 'readonly' : 'plain';
        }
        return entry && !entry.locked ? 'editable' : 'plain';
    };
    const losesMeals = skip.filter(d => datesWithOrders.includes(d));

    const setSkipped = (dates, value) => {
        if (saving) return;
        setDraft(prev => {
            const next = new Set(prev);
            dates.forEach(d => (value ? next.add(d) : next.delete(d)));
            return next;
        });
    };

    const handleSave = async () => {
        setSaving(true);
        setError('');
        try {
            const response = await mealPlanService.updateSkips(customerToken, { skip, unskip });
            onSaved({ skip, unskip, schedule: response.data.schedule });
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to update your deliveries.');
            setSaving(false);
        }
    };

    const cellHeight = isWide ? 62 : 54;

    // Past-cutoff dates render like any other day, so the customer never sees cutoff wording here.
    const renderCell = (cell, key) => {
        const wrapper = {
            padding: isWide ? '3px' : '2px',
            height: `${cellHeight}px`,
            boxSizing: 'border-box',
        };
        if (!cell) return <div key={key} style={wrapper} />;
        const { date, entry } = cell;
        const dayOfMonth = Number(date.slice(8));

        const state = cellState(date, entry);
        if (state === 'plain') {
            const isToday = date === today;
            return (
                <div key={key} style={wrapper}>
                    <div style={{
                        height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: isWide ? '18px' : '16px', lineHeight: 1, color: '#bdb6af',
                    }}>
                        <span style={{ position: 'relative' }}>
                            {dayOfMonth}
                            {/* Absolute so the dot doesn't push today's number off the row's baseline. */}
                            {isToday && <span style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: '4px', width: '4px', height: '4px', borderRadius: '50%', backgroundColor: COLORS.orange }} />}
                        </span>
                    </div>
                </div>
            );
        }

        const readOnly = state === 'readonly' || state === 'emptySkipped';
        const skipped = !readOnly && draft.has(date);
        // An empty plan day this week shows as "No meals yet" until it's actually skipped.
        const noMeals = !skipped && currentWeek.has(date) && !withMeals.has(date);
        const palette = skipped ? SKIPPED : noMeals ? NO_MEALS : DELIVERING;
        const changed = !readOnly && skipped !== saved.has(date);
        return (
            <div key={key} style={wrapper}>
                <button className="sdm-bare"
                    type="button"
                    onClick={() => !readOnly && setSkipped([date], !skipped)}
                    disabled={saving || readOnly}
                    aria-pressed={skipped}
                    aria-label={`${formatDeliveryDate(date)}: ${skipped ? 'skipped' : noMeals ? 'no meals yet' : 'delivering'}`}
                    title={readOnly ? (noMeals ? 'No meals selected for this delivery' : 'Meals scheduled') : skipped ? 'Tap to bring this delivery back' : 'Tap to skip this delivery'}
                    style={{
                        width: '100%', height: '100%', padding: 0, boxSizing: 'border-box',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        gap: isWide ? '4px' : '2px', borderRadius: '10px', fontFamily: 'inherit',
                        border: `2px ${skipped ? 'dashed' : 'solid'} ${palette.border}`,
                        backgroundColor: palette.bg, color: palette.text, cursor: readOnly ? 'default' : 'pointer',
                        boxShadow: changed ? `0 0 0 2px ${PRIMARY}` : 'none',
                        transition: 'background-color 0.15s, border-color 0.15s',
                        touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
                    }}
                >
                    {!skipped && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                            <TruckIcon size={isWide ? 18 : 15} color={noMeals ? TRUCK_RED : TRUCK_GREEN} />
                            {noMeals && <span style={{ fontSize: isWide ? '11px' : '8px', lineHeight: 1, color: TRUCK_RED }}>(0)</span>}
                        </span>
                    )}
                    <span style={{
                        fontSize: isWide ? '18px' : '16px', fontWeight: 700, lineHeight: 1,
                        textDecoration: skipped ? 'line-through' : 'none',
                    }}>
                        {dayOfMonth}
                    </span>
                    {skipped && <span style={{ fontSize: isWide ? '11px' : '8px', lineHeight: 1 }}>Skipped</span>}
                </button>
            </div>
        );
    };

    // The action sits on the row holding the week's Saturday and covers the whole Sun–Sat week,
    // so a week split across months is handled from the later month only.
    const editableByWeek = new Map();
    months.forEach(({ rows }) => rows.forEach(row => row.forEach(c => {
        if (!c || cellState(c.date, c.entry) !== 'editable') return;
        const week = addDays(c.date, -weekdayOf(c.date));
        editableByWeek.set(week, [...(editableByWeek.get(week) || []), c.date]);
    })));

    const renderWeekAction = (row, rowId) => {
        const saturday = row[6]?.date;
        const editable = saturday ? editableByWeek.get(addDays(saturday, -6)) || [] : [];
        const allSkipped = editable.length > 0 && editable.every(d => draft.has(d));
        return (
            <div key={`${rowId}-action`} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'flex-end', height: `${cellHeight}px`,
            }}>
                {editable.length > 0 && (
                    <button className="sdm-bare"
                        type="button"
                        onClick={() => setSkipped(editable, !allSkipped)}
                        disabled={saving}
                        style={{ ...linkButton, fontSize: isWide ? '14px' : '12px', textAlign: 'right', lineHeight: 1.2, minHeight: '44px', minWidth: '44px', touchAction: 'manipulation' }}
                    >
                        {allSkipped ? 'Resume' : 'Skip'}
                        {isWide ? ' ' : <br />}
                        week
                    </button>
                )}
            </div>
        );
    };

    // minmax(0, …) lets day columns shrink, so the week action is never pushed past the edge.
    const columns = isWide ? 'repeat(7, minmax(0, 1fr)) 104px' : 'repeat(7, minmax(0, 1fr)) 58px';

    return (
        <div
            onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}
            style={{
                position: 'fixed', inset: 0, backgroundColor: 'rgba(43, 33, 24, 0.55)', zIndex: 1000,
                display: 'flex', alignItems: isWide ? 'center' : 'flex-end', justifyContent: 'center',
                padding: isWide ? '24px' : '0 10px',
            }}
        >
            <div style={{
                position: 'relative', backgroundColor: '#fff', width: '100%', maxWidth: isWide ? '640px' : 'none',
                maxHeight: isWide ? '92vh' : 'calc(100% - 12px)', borderRadius: isWide ? '16px' : '20px 20px 0 0',
                display: 'flex', flexDirection: 'column', overflow: 'hidden',
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.18)', fontFamily: FONTS.sans, color: INK,
            }}>
                <div style={{ padding: isWide ? '26px 28px 10px' : '10px 16px 6px', position: 'relative', flexShrink: 0 }}>
                    {!isWide && (
                        <div style={{ width: '40px', height: '5px', borderRadius: '3px', background: '#CFC8C1', margin: '0 auto 14px' }} />
                    )}
                    <h3 style={{ margin: 0, paddingRight: '40px', fontSize: isWide ? '26px' : '22px', lineHeight: 1.2, fontWeight: 900 }}>
                        Manage upcoming deliveries
                    </h3>
                    <p style={{ margin: '6px 0 0', fontSize: isWide ? '15px' : '14px', lineHeight: 1.4, color: INK_MUTED }}>
                        Tap a delivery to skip it, or skip a whole week at once.
                    </p>
                    <button className="sdm-bare sdm-close"
                        onClick={onClose}
                        disabled={saving}
                        aria-label="Close"
                        style={{
                            position: 'absolute', top: isWide ? '10px' : '6px', right: isWide ? '12px' : '4px',
                            width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            border: 'none', background: 'none', padding: 0, fontSize: '26px', lineHeight: 1, color: INK_MUTED, cursor: 'pointer',
                        }}
                    >
                        ×
                    </button>
                </div>

                <div
                    onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
                    onTouchEnd={(e) => {
                        if (touchStartX.current == null) return;
                        const dx = e.changedTouches[0].clientX - touchStartX.current;
                        touchStartX.current = null;
                        if (Math.abs(dx) < 60) return;
                        const target = months[monthIndex + (dx < 0 ? 1 : -1)];
                        if (target) setMonthKey(target.key);
                    }}
                    style={{ flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain', padding: isWide ? '16px 28px 20px' : '12px 12px 16px' }}
                >
                    {!schedule && !error && (
                        <p style={{ color: INK_MUTED, textAlign: 'center', padding: '60px 0' }}>Loading your deliveries…</p>
                    )}
                    {schedule && month && (
                        <>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                                <div style={{ fontSize: isWide ? '20px' : '18px', fontWeight: 900 }}>{month.label}</div>
                                <div style={{ display: 'flex', gap: '4px' }}>
                                    <ArrowButton direction="prev" disabled={monthIndex === 0} onClick={() => setMonthKey(months[monthIndex - 1].key)} />
                                    <ArrowButton direction="next" disabled={monthIndex === months.length - 1} onClick={() => setMonthKey(months[monthIndex + 1].key)} />
                                </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: columns, gridTemplateRows: `auto repeat(${month.rows.length}, ${cellHeight}px)` }}>
                                {WEEKDAYS.map((w, i) => (
                                    <div key={i} style={{ textAlign: 'center', fontSize: '13px', color: INK_MUTED, paddingBottom: '6px' }}>{w}</div>
                                ))}
                                <div />
                                {month.rows.map((row, rowIndex) => (
                                    <React.Fragment key={`${month.key}-${rowIndex}`}>
                                        {row.map((cell, i) => renderCell(cell, `${month.key}-${rowIndex}-${i}`))}
                                        {renderWeekAction(row, `${month.key}-${rowIndex}`)}
                                    </React.Fragment>
                                ))}
                            </div>
                        </>
                    )}
                </div>

                <div style={{
                    flexShrink: 0, borderTop: `1px solid ${DIVIDER}`, padding: isWide ? '12px 28px' : '8px 16px',
                    // Separate from the shorthand so a browser without env() keeps the rest of the padding.
                    paddingBottom: isWide ? '12px' : 'calc(8px + env(safe-area-inset-bottom))',
                    boxShadow: isWide ? 'none' : '0 -6px 12px rgba(43,33,24,0.06)',
                }}>
                    {losesMeals.length > 0 && (
                        <div style={{ fontSize: '13px', color: '#6B3F12', backgroundColor: '#FDEBD3', padding: '8px 12px', borderRadius: '8px', marginBottom: '12px' }}>
                            Your meal selections for {losesMeals.map(formatDeliveryDate).join(', ')} will be removed.
                        </div>
                    )}
                    {error && (
                        <div style={{ fontSize: '13px', color: '#721c24', backgroundColor: '#f8d7da', padding: '8px 12px', borderRadius: '8px', marginBottom: '12px' }}>
                            {error}
                        </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                        <div>
                            {editableSkipped.length > 0 && (
                                <button className="sdm-bare" type="button" onClick={() => setSkipped(editableSkipped, false)} style={{ ...linkButton, fontSize: '15px' }}>
                                    Reset
                                </button>
                            )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: isWide ? '12px' : '10px' }}>
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={saving}
                                style={{
                                    minHeight: isWide ? '44px' : '48px', padding: '0 22px', borderRadius: '8px',
                                    border: '1.5px solid #3A322B', background: '#fff', color: INK,
                                    fontFamily: 'inherit', fontSize: '16px', fontWeight: 700, cursor: 'pointer',
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSave}
                                disabled={!hasChanges || saving}
                                style={{
                                    minHeight: isWide ? '44px' : '48px', padding: '0 28px',
                                    borderRadius: '8px', border: 0, whiteSpace: 'nowrap',
                                    background: hasChanges && !saving ? PRIMARY : '#d8d3ce', color: '#fff',
                                    fontFamily: 'inherit', fontSize: '16px', fontWeight: 700,
                                    cursor: hasChanges && !saving ? 'pointer' : 'not-allowed',
                                }}
                            >
                                {saving ? 'Saving…' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SkipDeliveriesModal;
